import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, Mail, Shield, CheckCircle2 } from 'lucide-react';
import { supabase, TABLES } from '../api/db';
import { useAuth } from '../contexts/AuthContext';
import { Task } from '../types';

type SendResult = {
  sent?: boolean;
  reason?: string;
  toRequester?: boolean;
  toOwner?: boolean;
  selfSummary?: boolean;
};

/**
 * Sends a formatted task summary by email (Resend / Cloud Function).
 * Delivers a copy to the signed-in user; notifies the task owner with Reply-To set to the requester.
 */
const TaskGetEmail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resultMeta, setResultMeta] = useState<SendResult | null>(null);

  useEffect(() => {
    if (!taskId || !currentUser) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data: taskRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', taskId).maybeSingle();
        if (!taskRow) {
          setError('Task not found.');
          setTask(null);
          return;
        }
        setTask({ ...(taskRow as object) } as Task);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load.');
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId, currentUser]);

  const handleSend = async () => {
    if (!taskId || !currentUser?.email) {
      setSendError('Your account needs an email address to receive the summary.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const { data, error } = await supabase.functions.invoke('send-task-details-email', { body: { taskId } });
      if (error) throw error;
      const result = data as SendResult;
      setResultMeta(result);
      if (result.sent) {
        setSuccess(true);
      } else {
        setSendError(
          result.reason
            ? `Could not send: ${result.reason}`
            : 'Email could not be sent. Try again later.'
        );
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setSending(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center px-4">
        <Link to="/login" className="text-tech-cyan hover:underline">
          Sign in to continue
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center">
        <Loader className="w-10 h-10 text-tech-cyan animate-spin" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text px-4 py-12 flex flex-col items-center justify-center">
        <AlertCircle className="w-12 h-12 text-amber-400 mb-3" />
        <p className="text-tech-text-muted mb-6">{error || 'Unavailable.'}</p>
        <button
          type="button"
          onClick={() => navigate('/task-dashboard')}
          className="text-tech-cyan hover:underline"
        >
          Back to Task hub
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text px-4 py-10 md:py-24 bg-tech-mesh">
      <div className="max-w-lg mx-auto">
        <button
          type="button"
          onClick={() => navigate(`/tasks/${task.id}`)}
          className="flex items-center gap-2 text-tech-text-muted hover:text-tech-cyan mb-8 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to task
        </button>

        <div className="rounded-2xl border border-tech-cyan/25 bg-gradient-to-b from-tech-cyan/10 to-transparent p-1 shadow-xl shadow-tech-cyan/5">
          <div className="rounded-[14px] bg-tech-darker/95 border border-tech-border px-6 py-8 md:px-10 md:py-10">
            <div className="w-14 h-14 rounded-2xl bg-tech-cyan/15 border border-tech-cyan/30 flex items-center justify-center mb-6">
              <Mail className="w-7 h-7 text-tech-cyan" aria-hidden />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-tech-text mb-2">Email task summary</h1>
            <p className="text-tech-text-muted text-sm mb-4">
              <span className="text-tech-text font-medium">{task.title}</span>
            </p>

            {success ? (
              <div className="rounded-xl border border-tech-accent/40 bg-tech-accent/10 px-4 py-5 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-tech-accent shrink-0 mt-0.5" />
                  <div className="text-sm text-tech-text leading-relaxed">
                    <p className="font-semibold text-tech-text mb-2">Check your inbox</p>
                    {resultMeta?.selfSummary ? (
                      <p>We sent a formatted summary of your task to <strong>{currentUser.email}</strong>.</p>
                    ) : (
                      <>
                        <p className="mb-2">
                          We sent a full task summary to <strong>{currentUser.email}</strong>.
                        </p>
                        {resultMeta?.toOwner && (
                          <p className="text-tech-text-muted">
                            The task owner was also notified with the same details. Their inbox shows{' '}
                            <strong className="text-tech-text">Reply-To: your address</strong> so they can respond
                            directly.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-tech-text-muted mb-6 leading-relaxed">
                  We&apos;ll send a polished HTML email with this task&apos;s title, summary, requirements, acceptance
                  criteria, tags, and metadata. You get a copy; if you&apos;re not the owner, the task owner is
                  notified and can reply to you by email.
                </p>

                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-tech-dark disabled:opacity-50 shadow-lg shadow-tech-cyan/20"
                  style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                >
                  {sending ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send email summary
                    </>
                  )}
                </button>

                {sendError && (
                  <p className="mt-4 text-sm text-amber-200" role="alert">
                    {sendError}
                  </p>
                )}
              </>
            )}

            <div className="flex items-start gap-2 text-xs text-tech-text-muted border-t border-tech-border pt-6 mt-8">
              <Shield className="w-4 h-4 text-tech-accent flex-shrink-0 mt-0.5" />
              <span>
                Sent by GNK Continuum via our mail provider. For structured offers, use{' '}
                <strong className="text-tech-text">responses</strong> on the task page.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskGetEmail;
